/**
 * Anhang-II-Bruecke — EINE Tabelle, die die drei Kataloge der App zu Anhang II
 * BaustellV (besonders gefaehrliche Arbeiten, Fassung ab 01.04.2023)
 * zusammenhaelt:
 *
 *   Nummer 1-10         so zaehlt der Anhang II, so rechnet die Planungsphase
 *                       (`pruefung.anhang2[].nummer`, `anhang2Antworten`).
 *   DANGEROUS_WORKS-Id  so speichert die Vorankuendigung Punkt 11
 *                       (`selectedDangerousWorks`, `constants.ts`).
 *   Handbuch-Wortlaut   so speichert das Handbuch je Gewerk
 *                       (`handbuch_gewerke.anhang_ii`, `handbuch/anhangII.ts`).
 *   Merkmal             der Ja/Nein-Schluessel der Planungsphase
 *                       (`ProjektMerkmale`, gleich wie in `ANHANG_2_ARBEITEN`).
 *
 * Eine Zeile je Vorankuendigungs-Id, nicht je Nummer: Nr. 1 und Nr. 2 sind in
 * der Vorankuendigung auf je zwei Ids verteilt, und nur auf dieser Ebene laesst
 * sich sagen, WELCHER Handbuch-Wortlaut zu „absturz" gehoert — sonst schluege
 * das Handbuch bei angekreuztem Absturz auch die Verschuettung vor. Zeilen
 * derselben Nummer stehen untereinander.
 *
 * Kein Vorschriften-Wortlaut hier. Die Bezeichnungen der Nummern stehen in
 * `fragenkatalog.ts` (ANHANG_2_ARBEITEN), die Labels der Vorankuendigung in
 * `constants.ts`, die Handbuch-Wortlaute in `anhangII.ts`. Diese Datei
 * verknuepft nur — und der Test prueft, dass alle drei Seiten vollstaendig
 * angebunden sind.
 *
 * Bekannte Luecken (bewusst nicht hier entschieden):
 *   - Der Handbuch-Katalog fuehrt fuer Nr. 2 nur Gefahrstoffe Kat. 1A/1B und
 *     Biostoffe RG 3/4; die explosiven/entzuendbaren/akut toxischen Stoffe
 *     (Id `explosiv`) haben dort keinen Wortlaut → `handbuch: []`.
 *   - Der Handbuch-Wortlaut „Tauchgeraete / Druckluft" deckt Nr. 7 UND Nr. 8.
 *
 * Reine Daten — kein DOM, kein React, keine Supabase. `anhangII.ts` importiert
 * diese Datei; hierher fliesst von dort nur ein Typ (kein Laufzeitkreis).
 */

import { DANGEROUS_WORKS } from './constants';
import type { AnhangIIArbeit } from '../components/handbuch/anhangII';
import type { ProjektMerkmale } from '../components/planungsphase/types';

/** Id eines Eintrags in `DANGEROUS_WORKS` — so steht sie in `selectedDangerousWorks`. */
export type DangerousWorkId = (typeof DANGEROUS_WORKS)[number]['id'];

/** Die zehn Nummern des Anhangs II BaustellV. */
export type AnhangIINummer = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export const ANHANG_2_NUMMERN: readonly AnhangIINummer[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export interface Anhang2Zeile {
    /** Nummer im Anhang II BaustellV. */
    nummer: AnhangIINummer;
    /** Id in `DANGEROUS_WORKS` bzw. `vorankuendigung.selectedDangerousWorks`. */
    id: DangerousWorkId;
    /**
     * Wortlaut(e) aus `ANHANG_II_ARBEITEN`, die dieselbe Arbeit meinen. Leer,
     * wenn der Handbuch-Katalog die Arbeit nicht fuehrt.
     */
    handbuch: readonly AnhangIIArbeit[];
    /**
     * Ja/Nein-Merkmal der Planungsphase, wortgleich mit `ANHANG_2_ARBEITEN`.
     * Nr. 1 hat keines: dort rechnet `pruefeAnhang2` aus `grabentiefeM` (> 5)
     * und `absturzhoeheM` (> 7).
     */
    merkmal?: keyof ProjektMerkmale;
}

export const ANHANG_2_BRUECKE: readonly Anhang2Zeile[] = [
    { nummer: 1, id: 'versinken', handbuch: ['Verschüttungsgefahr > 5m (Baugruben/Gräben)'] },
    { nummer: 1, id: 'absturz', handbuch: ['Absturzgefahr > 7m'] },
    { nummer: 2, id: 'explosiv', handbuch: [], merkmal: 'gefahrstoffeAnhang2' },
    {
        nummer: 2,
        id: 'chemisch',
        handbuch: [
            'Gefahrstoffe Kategorie 1A/1B (karzinogen, mutagen, reproduktionstoxisch)',
            'Biologische Arbeitsstoffe Risikogruppe 3 oder 4',
        ],
        merkmal: 'gefahrstoffeAnhang2',
    },
    { nummer: 3, id: 'strahlung', handbuch: ['Ionisierende Strahlung (Kontroll-/Überwachungsbereiche)'], merkmal: 'strahlung' },
    { nummer: 4, id: 'hochspannung', handbuch: ['Arbeiten in < 5m Abstand von Hochspannungsleitungen'], merkmal: 'freileitung' },
    { nummer: 5, id: 'ertrinken', handbuch: ['Unmittelbare Gefahr des Ertrinkens'], merkmal: 'gewaesser' },
    { nummer: 6, id: 'untertage', handbuch: ['Brunnenbau, unterirdische Erdarbeiten, Tunnelbau'], merkmal: 'tunnelbau' },
    { nummer: 7, id: 'tauchen', handbuch: ['Arbeiten mit Tauchgeräten / Druckluft'], merkmal: 'taucharbeiten' },
    { nummer: 8, id: 'druckluft', handbuch: ['Arbeiten mit Tauchgeräten / Druckluft'], merkmal: 'druckluft' },
    { nummer: 9, id: 'sprengen', handbuch: ['Arbeiten mit Sprengstoff'], merkmal: 'sprengarbeiten' },
    { nummer: 10, id: 'schwere_bauteile', handbuch: ['Auf-/Abbau von Massivbauelementen mit kraftbetriebenen Hebezeugen'], merkmal: 'fertigteilmontage' },
];

// ─── Hilfen ──────────────────────────────────────────────────────────────────

export const istAnhangIINummer = (n: unknown): n is AnhangIINummer =>
    typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 10;

const eindeutig = <T>(werte: readonly T[]): T[] => Array.from(new Set(werte));

const aufsteigend = (nummern: readonly AnhangIINummer[]): AnhangIINummer[] =>
    eindeutig(nummern).sort((a, b) => a - b);

/** Alle Zeilen einer Nummer, in Tabellenreihenfolge. */
export const zeilenFuerNummer = (nummer: number): Anhang2Zeile[] =>
    ANHANG_2_BRUECKE.filter(z => z.nummer === nummer);

/** Die Zeile einer Vorankuendigungs-Id; `null` fuer eine unbekannte Id. */
export const zeileFuerId = (id: string): Anhang2Zeile | null =>
    ANHANG_2_BRUECKE.find(z => z.id === id) ?? null;

/** Anhang-II-Nummer einer Vorankuendigungs-Id; `null` fuer eine unbekannte Id. */
export const nummerFuerId = (id: string): AnhangIINummer | null => zeileFuerId(id)?.nummer ?? null;

/** Nummern (aufsteigend, ohne Doppelte) zu einer Auswahl aus `selectedDangerousWorks`. */
export const nummernFuerIds = (ids: readonly string[] | null | undefined): AnhangIINummer[] =>
    aufsteigend((ids || []).map(nummerFuerId).filter((n): n is AnhangIINummer => n !== null));

/**
 * Vorankuendigungs-Ids zu Anhang-II-Nummern, in Tabellenreihenfolge. Nr. 1
 * liefert ZWEI Ids (Versinken UND Absturz) — wer nur eine meint, muss das aus
 * den Zahlen (`grabentiefeM`, `absturzhoeheM`) selbst entscheiden.
 */
export const idsFuerNummern = (nummern: readonly number[] | null | undefined): DangerousWorkId[] => {
    const gesucht = new Set((nummern || []).filter(istAnhangIINummer));
    return ANHANG_2_BRUECKE.filter(z => gesucht.has(z.nummer)).map(z => z.id);
};

/** Handbuch-Wortlaute zu einer Auswahl aus `selectedDangerousWorks`, in Katalogreihenfolge, ohne Doppelte. */
export const handbuchFuerIds = (ids: readonly string[] | null | undefined): AnhangIIArbeit[] => {
    const gesucht = new Set(ids || []);
    return eindeutig(ANHANG_2_BRUECKE.filter(z => gesucht.has(z.id)).flatMap(z => z.handbuch));
};

/** Anhang-II-Nummern eines Handbuch-Wortlauts (aufsteigend); leer fuer einen fremden Wortlaut. */
export const nummernFuerHandbuch = (wortlaut: string): AnhangIINummer[] =>
    aufsteigend(ANHANG_2_BRUECKE.filter(z => (z.handbuch as readonly string[]).includes(wortlaut)).map(z => z.nummer));

/** Vorankuendigungs-Ids zu Handbuch-Wortlauten, in Tabellenreihenfolge, ohne Doppelte. */
export const idsFuerHandbuch = (wortlaute: readonly string[] | null | undefined): DangerousWorkId[] => {
    const gesucht = new Set(wortlaute || []);
    return ANHANG_2_BRUECKE.filter(z => z.handbuch.some(w => gesucht.has(w))).map(z => z.id);
};

/** Ja/Nein-Merkmal der Planungsphase zu einer Nummer; `null`, wo es keines gibt (Nr. 1). */
export const merkmalFuerNummer = (nummer: number): keyof ProjektMerkmale | null =>
    zeilenFuerNummer(nummer).find(z => z.merkmal)?.merkmal ?? null;

/** Anhang-II-Nummern, die an einem Planungsmerkmal haengen (aufsteigend). */
export const nummernFuerMerkmal = (merkmal: keyof ProjektMerkmale): AnhangIINummer[] =>
    aufsteigend(ANHANG_2_BRUECKE.filter(z => z.merkmal === merkmal).map(z => z.nummer));

/** Label der Vorankuendigung zu einer Id; `null` fuer eine unbekannte Id. */
export const labelFuerId = (id: string): string | null =>
    DANGEROUS_WORKS.find(w => w.id === id)?.label ?? null;
